import { safeParseISO } from "@utils/date";
import { ProductVisibilityTypes, SalesIncentiveType } from "@utils/enums";
import { isDefined } from "@utils/helpers";

import { Translate } from "@redux/modules/value/translations/translations.provider";

import {
    DisplayPromotion,
    ECommerceModel,
    EDLPImage,
} from "@components/Product/ECommerce/eCommerce.types";

import { FullProductApiModel } from "lib/@types/service/product/api/product.api.model";
import { ProductPromotion } from "lib/@types/service/product/shared";

import { deliveryItemMapper } from "./mappers/deliveryItem.mapper";
import { getPriceAfterCashbackIsApplied } from "./mappers/price.mapper";
import {
    isClickAndCollectAvailable,
    isHomeDeliveryAvailable,
    productMapper,
} from "./mappers/product.mapper";
import { getCashbackPromotion } from "./mappers/promotion.mapper";

const BLACK_LAST = [
    SalesIncentiveType.GratisTransport,
    SalesIncentiveType.Cashback,
];

const filterPromotions = (promotion: ProductPromotion) => {
    return (
        !!promotion.marketingText &&
        !!promotion.promotionType &&
        !BLACK_LAST.find(bl => bl === promotion.promotionType)
    );
};

const isExclusivelyOnline = (product: FullProductApiModel) => {
    return (
        isHomeDeliveryAvailable(product.productVisibilityType) &&
        !isClickAndCollectAvailable(product.productVisibilityType)
    );
};

const EcommerceProductAdapter = (
    product: FullProductApiModel,
    cashbackPagePath: string,
    contextAwareXfPath: string,
    edlpimage: EDLPImage,
    t: Translate,
): ECommerceModel => {
    const cashbackPromotion = getCashbackPromotion(
        product?.price?.promotions || [],
    );

    const deliveryOptions =
        (product?.deliveryTypes || []).map((type: string) =>
            deliveryItemMapper(type, product),
        ) || [];

    const visibilityType =
        product?.productVisibilityType || ProductVisibilityTypes.Default;

    const ecommerceProduct: ECommerceModel = {
        cashbackPagePath,
        edlpimage,
        contextAwareXfPath,
        cashbackAmount: cashbackPromotion?.cashbackAmount || null,
        cashbackPromotion: cashbackPromotion,
        cashbackUnit: cashbackPromotion?.cashbackUnit || null,
        conversionUnit: product?.price?.conversionUnit || null,
        deliveryCost: product?.price?.deliveryCost || 0,
        deliveryTypes: product?.deliveryTypes,
        deliveryOptions: deliveryOptions,
        discountPercentage: Math.floor(product?.price?.discountPercentage || 0),
        product: productMapper(product),
        everyDayLowPrice: product?.everyDayLowPrice || false,
        minimumOrderQuantity: product?.minimumOrderQuantity,
        priceAfterCashbackIsApplied: getPriceAfterCashbackIsApplied(
            product,
            cashbackPromotion,
        ),
        promotions:
            Object.values(
                product?.price?.promotions
                    ?.filter(filterPromotions)
                    .reduce<Record<string, Array<DisplayPromotion>>>(
                        (acc, prom) => {
                            const key = `${prom.marketingText}-${prom.promotionType}`;
                            const promotion = {
                                marketingText: prom.marketingText ?? undefined,
                                validFrom: safeParseISO(prom.validFrom),
                                validTo: safeParseISO(prom.validTo),
                                periodText: prom.periodText,
                            };

                            if (acc[key]) {
                                acc[key].push(promotion);
                            } else {
                                acc[key] = [promotion];
                            }

                            return acc;
                        },
                        {},
                    ) || {},
            ).reduce<Array<DisplayPromotion>>((acc, groupedPromList) => {
                const oldestValidToPromotion = groupedPromList
                    .filter(isDefined)
                    .sort((a, b) => {
                        // Sort by validTo date, descending
                        // If validTo is not present, it should be last
                        const aDate = a.validTo?.getTime() ?? 0;
                        const bDate = b.validTo?.getTime() ?? 0;
                        return bDate - aDate;
                    })
                    .at(0);

                if (oldestValidToPromotion) {
                    acc.push(oldestValidToPromotion);
                }

                return acc;
            }, []) || [],
        roundingValue: product?.roundingValue,
        sku: product?.sku,
        ean: product?.ean,
        currentPrice: product?.price?.currentPrice || null,
        strikethroughPrice: product?.price?.strikethroughPrice || null,
        currentConversionPrice: product?.price?.currentConversionPrice || null,
        strikethroughConversionPrice:
            product?.price?.strikethroughConversionPrice || null,
        visibilityType: visibilityType as ProductVisibilityTypes,
        ecochequesAccepted: product?.ecochequesAccepted,
        webOnly: product?.webOnly,
        clickAndCollectAvailable: isClickAndCollectAvailable(
            product.productVisibilityType,
        ),
        isExclusivelyOnline: isExclusivelyOnline(product),
    };

    if (product.doubleBonusPoints) {
        ecommerceProduct.promotions.push({
            marketingText: t("productDetail.promo.doubleBonusPoints"),
            validFrom: undefined,
            validTo: undefined,
        });
    }

    return ecommerceProduct;
};

export { EcommerceProductAdapter };
